<script setup>
import {defineProps, watch} from "vue";
import {onMounted} from "vue";
import {usePortalContentAndDesignStore} from "../../stores/partners/portalContentAndDesignStore";
import {hexToRgbString, injectStyleBlock, isCustomColor} from "../../helpers/common";

    const props = defineProps({
        data: {
            type: Object,
            required: false
        },
        authView: {
            type: Boolean,
            default: false
        },
        isPreview: {
            type: Boolean,
            default: false
        },
        socialRegisterErrors: {
            type: [Object, Array],
            default: null
        },
        socialLoginErrors: {
            type: [Object, Array],
            default: null
        },
    });

    const store = usePortalContentAndDesignStore()

    onMounted(() => {
        store.initializePortalContent(props.data, props.authView, props.isPreview);
        if (props.socialLoginErrors) {
            store.loginFormErrors.validationErrors = props.socialLoginErrors;
        }
        if (props.socialRegisterErrors) {
            store.signupFormErrors.validationErrors = props.socialRegisterErrors;
        }
        store.portalInitialized = true
    });

    watch(() => store.portalLayoutSettings, (newSettings, oldSettings) => {
        // As all classes are initialized in blade files in the first place, there is no need to run this code on initial load.
        if(store.liveDesignerEnabled && Object.keys(oldSettings).length > 0) {
            let dynamicCss = ''

            if (isCustomColor(newSettings.public_page_background_color)) {
                dynamicCss += `
                    .bg-custom-bg {
                        background-color: rgba(${hexToRgbString(newSettings.public_page_background_color)});
                    }
                `;
            }

            // Two different classes for the same class. Check if this is intentional. (copied from pub/affiliate.blade.php and auth/affiliate.blade.php)
            if (isCustomColor(newSettings.portal_layout_background_color)) {
                dynamicCss += `
                    .bg-custom-bg {
                        background-color: rgba(${ hexToRgbString(newSettings.portal_layout_background_color) }, var(--tw-bg-opacity));
                    }
                `;
            }

            if (isCustomColor(newSettings.public_page_background_gradient_color)) {
                dynamicCss += `
                    .from-custom-gradient-bg {
                        background: linear-gradient(rgba(${hexToRgbString(newSettings.public_page_background_gradient_color)}), transparent);
                    }
                `;
            }

            if (isCustomColor(newSettings.public_page_content_button_color)) {
                dynamicCss += `
                    .bg-custom-content-btn {
                        background-color: rgba(${hexToRgbString(newSettings.public_page_content_button_color)});
                        border-color: rgba(${hexToRgbString(newSettings.public_page_content_button_color)});
                        color: #ffffff;
                    }
                `;
            }

            if (isCustomColor(newSettings.public_page_text_color)) {
                dynamicCss += `
                    .text-custom-text {
                        color: rgba(${hexToRgbString(newSettings.public_page_text_color)}, var(--tw-text-opacity));
                    }
                    .bg-custom-text {
                        background-color: rgba(${hexToRgbString(newSettings.public_page_text_color)}, var(--tw-bg-opacity));
                    }
                    .border-custom-text {
                        border-color: rgba(${hexToRgbString(newSettings.public_page_text_color)}, var(--tw-border-opacity));
                    }
                `;
            }

            if (isCustomColor(newSettings.portal_layout_text_color)) {
                dynamicCss += `
                    .text-custom-text {
                        color: rgba(${hexToRgbString(newSettings.portal_layout_text_color)});
                    }
                `;
            }

            if (isCustomColor(newSettings.public_page_link_color)) {
                dynamicCss += `
                    .text-custom-link {
                        color: rgba(${hexToRgbString(newSettings.public_page_link_color)});
                    }
                `;
            }

            if (isCustomColor(newSettings.portal_layout_brand_primary_color)) {
                dynamicCss += `
                    .bg-brand-bg {
                        background-color: rgba(${hexToRgbString(newSettings.portal_layout_brand_primary_color)});
                    }
                    .bg-custom-primary {
                        background-color: rgba(${ hexToRgbString(newSettings.portal_layout_brand_primary_color) }, var(--tw-bg-opacity));
                    }
                    .text-custom-primary {
                            color: ${ newSettings.portal_layout_brand_primary_color };
                        }

                    .border-custom-primary {
                            border-color: rgba(${ hexToRgbString(newSettings.portal_layout_brand_primary_color) }, var(--tw-border-opacity));
                        }

                    .hover\:text-custom-primary:hover {
                            color: rgba(${ hexToRgbString(newSettings.portal_layout_brand_primary_color) }, var(--tw-text-opacity));
                        }

                    .hover\:bg-custom-primary:hover {
                            background-color: rgba(${ hexToRgbString(newSettings.portal_layout_brand_primary_color) }, var(--tw-bg-opacity));
                        }

                    .hover\:border-custom-primary:hover {
                            border-color: rgba(${ hexToRgbString(newSettings.portal_layout_brand_primary_color) }, var(--tw-border-opacity));
                        }
                `;
            }

            if (isCustomColor(newSettings.public_page_content_background_color)) {
                dynamicCss += `
                    .bg-content-bg {
                        background-color: rgba(${hexToRgbString(newSettings.public_page_content_background_color)});
                    }
                `;
            }

            if (isCustomColor(newSettings.public_page_content_text_color)) {
                dynamicCss += `
                    .text-custom-content-text {
                        color: rgba(${hexToRgbString(newSettings.public_page_content_text_color)});
                    }
                `;
            }

            if (isCustomColor(newSettings.public_page_content_link_color)) {
                dynamicCss += `
                    .text-custom-content-text a {
                        color: rgba(${hexToRgbString(newSettings.public_page_content_link_color)});
                    }
                `;
            }

            if (isCustomColor(newSettings.portal_layout_button_color)) {
                dynamicCss += `
                    .bg-custom-btn {
                        background-color: rgba(${ hexToRgbString(newSettings.portal_layout_button_color) }, var(--tw-bg-opacity));
                        border-color: rgba(${ hexToRgbString(newSettings.portal_layout_button_color) }}, var(--tw-border-opacity));
                        color: #ffffff;
                    }
                `;
            }

            if (isCustomColor(newSettings.portal_layout_link_color)) {
                dynamicCss += `
                    .text-custom-link {
                        color: rgba(${ hexToRgbString(newSettings.portal_layout_link_color) }, var(--tw-text-opacity));
                    }
                    .group:hover .group-hover\:text-custom-link {
                        --tw-text-opacity: 1;
                        color: rgba(${ hexToRgbString(newSettings.portal_layout_link_color) }, var(--tw-text-opacity));
                    }
                    .border-custom-border {
                        border-color: rgba(${ hexToRgbString(newSettings.portal_layout_link_color) }, var(--tw-text-opacity));
                    }
                `;
            }

            injectStyleBlock(dynamicCss, 'custom-style-colors');
        }
    }, { deep: true });
</script>
<template>

</template>
